"use client";

import { isCapacitor } from "@/lib/mobile/platform";
import { USAGE_ACTIONS, USER_ACTION_HEADER } from "@/lib/usageActions";
import { UrlLinks } from "@/shared/config";

let installed = false;
let redirecting = false;

/** A same-origin fetch started this soon after a click / key press / submit counts as user activity. */
const GESTURE_WINDOW_MS = 1000;
/** Next.js router headers on prefetch requests (framework internals: re-check when upgrading Next). */
const NEXT_ROUTER_PREFETCH = "next-router-prefetch";
const NEXT_ROUTER_SEGMENT_PREFETCH = "next-router-segment-prefetch";
let lastGestureAt = 0;

function markGesture() {
  lastGestureAt = Date.now();
}

/**
 * Adds `x-user-action: ui` to a same-origin fetch a user gesture just started, so click-driven
 * saves keep the session alive without every page using userFetch(). Polling, timers and
 * auto-refresh run without a gesture and stay unmarked, so an open tab still idles out.
 */
function tagUserAction(input: RequestInfo | URL, init?: RequestInit): [RequestInfo | URL, RequestInit | undefined] {
  if (Date.now() - lastGestureAt > GESTURE_WINDOW_MS) return [input, init];
  const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
  try {
    const target = new URL(url, window.location.href);
    if (target.origin !== window.location.origin) return [input, init];
    // The tab-focus session check must never count as activity (a click often focuses the tab).
    if (target.pathname === UrlLinks.api.sessionCheck) return [input, init];
  } catch {
    return [input, init];
  }
  const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
  if (headers.has(USER_ACTION_HEADER)) return [input, init];
  // A link prefetch is never a user action. Next strips these headers before middleware, so the
  // middleware can't tell; marked, the prefetches a click starts would be logged as page views.
  if (headers.has(NEXT_ROUTER_PREFETCH) || headers.has(NEXT_ROUTER_SEGMENT_PREFETCH)) return [input, init];
  headers.set(USER_ACTION_HEADER, USAGE_ACTIONS.UI);
  return [input, { ...init, headers }];
}

export function installAuthFetchInterceptor() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const originalFetch = window.fetch.bind(window);

  for (const type of ["pointerdown", "keydown", "submit"]) {
    document.addEventListener(type, markGesture, true);
  }

  window.fetch = async (input, init) => {
    const response = await originalFetch(...tagUserAction(input, init));
    if (response.status === 401 && !redirecting) {
      const { pathname, search } = window.location;
      if (!pathname.startsWith(UrlLinks.pages.login) && !pathname.startsWith(UrlLinks.pages.mobileEntry)) {
        redirecting = true;
        // The Capacitor app signs in through its own PKCE flow, not the web OIDC client.
        if (isCapacitor()) {
          window.location.href = UrlLinks.pages.mobileEntry;
          return response;
        }
        // The middleware says why it ended the session: "idle" → password again (/auth/reauth via
        // the login page), "expired" / "revoked" → normal login.
        const ended = response.headers.get("x-session-end");
        const reason = ended === "idle" || ended === "expired" || ended === "revoked" ? ended : "session_expired";
        const callbackUrl = encodeURIComponent(pathname + search);
        window.location.href = `${UrlLinks.pages.login}?reason=${reason}&callbackUrl=${callbackUrl}`;
      }
    }
    return response;
  };
}
